import React from 'react';
import type { ReactNode } from 'react';

/** Section heading within a chapter */
export function HSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mt-8 first:mt-0">
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">{title}</h3>
      {children}
    </div>
  );
}

/** Body paragraph — generous line-height for readability */
export function HParagraph({ children }: { children: ReactNode }) {
  return <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed mb-3">{children}</p>;
}

/** Metaphor callout box — the key design element that opens each chapter */
export function HMetaphor({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-lex-accent bg-lex-accent-soft p-4 mb-5">
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-sm font-semibold text-lex-accent">{title}</span>
      </div>
      <div className="text-sm text-lex-accent leading-relaxed">{children}</div>
    </div>
  );
}

/** Tip / "Good to know" box */
export function HTip({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20 px-4 py-3 mb-4">
      <span className="text-[10px] font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wide">Tip</span>
      <div className="text-sm text-amber-600 dark:text-amber-400 mt-1 leading-relaxed">{children}</div>
    </div>
  );
}

/** Warning / "Important" box */
export function HWarning({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg border border-rose-200 bg-rose-50 dark:border-rose-800 dark:bg-rose-900/20 px-4 py-3 mb-4">
      <span className="text-[10px] font-semibold text-rose-700 dark:text-rose-300 uppercase tracking-wide">Important</span>
      <div className="text-sm text-rose-600 dark:text-rose-400 mt-1 leading-relaxed">{children}</div>
    </div>
  );
}

/** Step-by-step instruction (numbered) */
export function HStep({ number, title, children }: { number: number; title: string; children: ReactNode }) {
  return (
    <div className="flex gap-3 mb-4">
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-lex-accent text-lex-accent-contrast text-xs font-bold flex items-center justify-center mt-0.5">
        {number}
      </div>
      <div className="flex-1">
        <div className="text-sm font-medium text-gray-900 dark:text-white mb-1">{title}</div>
        <div className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

/** Glossary term row */
export function HGlossaryTerm({ term, definition }: { term: string; definition: string }) {
  return (
    <div className="flex gap-4 py-3 border-b border-gray-100 dark:border-gray-800">
      <dt className="w-40 shrink-0 text-sm font-semibold text-gray-900 dark:text-white">{term}</dt>
      <dd className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">{definition}</dd>
    </div>
  );
}

/** Inline bold keyword highlight */
export function HBold({ children }: { children: ReactNode }) {
  return <strong className="font-semibold text-gray-900 dark:text-white">{children}</strong>;
}

/** Chapter wrapper with title and subtitle */
export function HChapter({ title, subtitle, children }: { title: string; subtitle: string; children: ReactNode }) {
  return (
    <div className="p-6 max-w-3xl">
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">{title}</h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{subtitle}</p>
      {children}
    </div>
  );
}
